import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { Banner, Button, Empty, ErrorBanner, Field, Panel, Spinner, TextInput } from '@shop/ui'
import { platformName, socialPosts, socialStatus } from '../social/api'
import { PostResults } from '../social/PostResults'

/**
 * حساباتُ التواصل — <b>الربطُ، والحساباتُ المربوطة، وآخرُ المنشورات</b>.
 *
 * <p>
 * ⚠ الربطُ يمرّ بنافذة فيسبوك الرسميّة: المالكُ يسجّل الدخولَ هناك ويختار الصفحة،
 * ولا يمرّ رمزٌ ولا كلمةُ سرٍّ من هنا. والرمزُ يُخزَّن مُعمًّى ولا يُعرض أبدًا.
 * </p>
 */
export default function Social() {
  const { can } = useAuth()
  const queries = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)
  const [showApp, setShowApp] = useState(false)

  // العودةُ من فيسبوك تحمل النتيجةَ في الرابط — تُعرض مرّةً ثمّ تُمحى منه.
  useEffect(() => {
    const connected = params.get('connected')
    const error = params.get('error')

    if (connected !== null) {
      setNotice({ tone: 'ok', text: `رُبط ${connected} حسابًا.` })
    } else if (error !== null) {
      setNotice({ tone: 'warn', text: error })
    }

    if (connected !== null || error !== null) {
      setParams({}, { replace: true })
    }
  }, [params, setParams])

  const status = useQuery({ queryKey: ['social-status'], queryFn: socialStatus })
  const posts = useQuery({ queryKey: ['social-posts', 'all'], queryFn: () => socialPosts() })

  const connect = useMutation({
    mutationFn: () => api<{ url: string }>('/api/v1/social/connect/meta'),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
  })

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api(`/api/v1/social/accounts/${id}`, { method: 'POST', body: { enabled } }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['social-status'] }),
  })

  const disconnect = useMutation({
    mutationFn: (id: string) => api(`/api/v1/social/accounts/${id}/disconnect`, { method: 'POST' }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['social-status'] }),
  })

  const manage = can(Perm.socialManage)

  return (
    <>
      <Panel
        title="حساباتُ التواصل"
        actions={<Link className="rounded-md bg-brand px-3 py-1.5 text-sm text-white" to="/social/new">منشورٌ جديد</Link>}
      >
        {notice && <Banner tone={notice.tone === 'ok' ? 'info' : 'warn'}>{notice.text}</Banner>}
        <ErrorBanner error={status.error ?? connect.error ?? toggle.error ?? disconnect.error} />
        {status.isPending && <Spinner />}

        {status.data && !status.data.keyReady && (
          <Banner tone="warn">مفتاحُ التعمية غيرُ مضبوطٍ على الخادم بعد — يضبطه من ينشر النظام (SOCIAL_TOKEN_KEY).</Banner>
        )}

        {status.data?.keyReady && manage && (!status.data.configured || showApp) && (
          <MetaAppSetup
            current={status.data.appId}
            onDone={() => {
              setShowApp(false)
              void queries.invalidateQueries({ queryKey: ['social-status'] })
            }}
          />
        )}

        {status.data?.configured && manage && !showApp && (
          <p className="mb-2 text-xs text-ink-soft">
            تطبيقُ Meta: <span dir="ltr">{status.data.appId}</span> ·{' '}
            <button type="button" className="underline" onClick={() => setShowApp(true)}>غيّره</button>
          </p>
        )}

        {status.data?.configured && manage && (
          <div className="mb-3">
            <Button tone="primary" disabled={connect.isPending} onClick={() => connect.mutate()}>
              {status.data.accounts.some((a) => a.connected) ? 'أعِد الربط / أضف صفحة' : 'اربط فيسبوك وإنستغرام'}
            </Button>
            <p className="mt-1 text-xs text-ink-soft">
              يفتح نافذةَ فيسبوك الرسميّة — تختار فيها صفحةَ المحلّ، ويُربط حسابُ إنستغرام المرتبطُ بها معها.
            </p>
          </div>
        )}

        {status.data && status.data.accounts.length === 0 && status.data.configured && (
          <Empty>لا حسابَ مربوطًا بعد.</Empty>
        )}

        <ul className="grid gap-2">
          {status.data?.accounts.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line px-3 py-2">
              {a.pictureUrl && <img src={a.pictureUrl} alt="" className="h-8 w-8 rounded-full" />}
              <span className="font-semibold">{platformName[a.platform]}</span>
              <span>{a.username ? `@${a.username}` : a.name}</span>
              {!a.connected && <span className="text-xs text-warn">مفكوكُ الربط</span>}

              {manage && a.connected && (
                <span className="mr-auto flex items-center gap-3">
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={a.enabled}
                      onChange={(e) => toggle.mutate({ id: a.id, enabled: e.target.checked })}
                    />
                    يُنشر عليه
                  </label>
                  <Button
                    tone="danger"
                    onClick={() => {
                      if (window.confirm(`فكُّ ربط ${platformName[a.platform]}؟ لن يُنشر عليه حتى يُعاد ربطُه.`)) {
                        disconnect.mutate(a.id)
                      }
                    }}
                  >
                    فكُّ الربط
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="آخرُ المنشورات">
        {posts.isPending && <Spinner />}
        {posts.data && posts.data.length === 0 && <Empty>لم يُنشر شيءٌ بعد.</Empty>}

        <div className="grid gap-2">
          {posts.data?.map((p) => <PostResults key={p.id} post={p} />)}
        </div>
      </Panel>
    </>
  )
}

/**
 * تطبيقُ Meta — <b>«المرحلة ٠» على الشاشة</b>: الخطواتُ، ورابطُ العودة ليُلصق كما هو، ثمّ
 * المعرّفُ والمفتاحُ السرّيّ. والسرُّ يُرسل مرّةً ويُخزَّن مُعمًّى، ولا يُعرض بعدها أبدًا.
 */
function MetaAppSetup({ current, onDone }: { current: string | null; onDone: () => void }) {
  const [appId, setAppId] = useState(current ?? '')
  const [secret, setSecret] = useState('')

  const redirect = useQuery({
    queryKey: ['social-redirect'],
    queryFn: () => api<{ redirectUri: string }>('/api/v1/social/meta-app/redirect-uri'),
  })

  const save = useMutation({
    mutationFn: () => api('/api/v1/social/meta-app', { method: 'PUT', body: { appId: appId.trim(), appSecret: secret.trim() } }),
    onSuccess: () => {
      setSecret('')
      onDone()
    },
  })

  return (
    <div className="mb-4 rounded-xl border border-line p-4">
      <h3 className="mb-2 font-semibold">إعدادُ تطبيق Meta — مرّةً واحدة</h3>
      <ol className="mb-3 list-decimal space-y-1 pr-5 text-sm">
        <li>حوّل حسابَ إنستغرام إلى «احترافيّ (Business)» واربطه بصفحة المحلّ على فيسبوك.</li>
        <li>
          من <a className="underline" href="https://developers.facebook.com/apps" target="_blank" rel="noopener">developers.facebook.com/apps</a>{' '}
          أنشئ تطبيقًا من نوع «Business»، وأضف إليه «Facebook Login for Business».
        </li>
        <li>
          في إعدادات تسجيل الدخول، الصق هذا في «Valid OAuth Redirect URIs» كما هو:
          <div className="mt-1 select-all rounded bg-surface-raised px-2 py-1 text-xs" dir="ltr">
            {redirect.data?.redirectUri ?? '…'}
          </div>
        </li>
        <li>من «الإعدادات ← الأساسيّة» انسخ «App ID» و«App Secret» والصقهما هنا.</li>
      </ol>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="App ID"><TextInput value={appId} onChange={setAppId} placeholder="123456789012345" /></Field>
        <Field label="App Secret" hint="يُخزَّن مُعمًّى، ولا يُعرض بعد الحفظ">
          <input
            type="password"
            autoComplete="off"
            className="w-full rounded-lg border border-line bg-surface px-3 py-2"
            dir="ltr"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </Field>
      </div>

      <ErrorBanner error={save.error} />

      <div className="mt-3">
        <Button tone="primary" disabled={save.isPending || !appId.trim() || !secret.trim()} onClick={() => save.mutate()}>
          حفظ
        </Button>
      </div>
    </div>
  )
}
